import React, { SyntheticEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import { Button, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getMetamaskWebsiteUrl } from 'helpers/links';

import { AccountActions } from 'store/reducers/account';
import { FollowingActions } from 'store/reducers/following';
import { SubscribingActions } from 'store/reducers/subscribing';
import { WalletActions } from 'store/reducers/wallet';

import CancelDialog from 'shared/dialogs/Cancel';
import MetaMaskModal from 'shared/dialogs/MetaMask';
import ProfileDialog from 'shared/dialogs/Profile';

import { CreateAccount } from 'entities/Account';

export interface FollowButtonProps {
  celebrityId: number;
  className?: string;
  isContained?: boolean;
  onSubscribe?: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
  btn: {
    '& svg': {
      margin: '0 5px 0 0',
    },
    width: '166px',
  },
}));

export default function SubscribeButton(props: FollowButtonProps) {
  const { celebrityId, className, isContained, onSubscribe } = props;
  const { classes, cx } = useStyles();

  const [open, setOpen] = useState(false);
  const [showMetaMaskDialog, setShowMetaMaskDialog] = useState<boolean>(false);
  const [showProfileDialog, setProfileDialog] = useState<boolean>(false);
  const [initValues] = useState<CreateAccount>({ nickName: '', email: '' });

  const {
    wallet: {
      error: walletError,
      wallet: { address },
    },
    metamask: { isMetaMask, loading: metamaskLoading },
    account: {
      account,
      role,
      loading: { role: loadingRole, account: loadingAccount },
      errors: { account: accountError },
    },
    subscribing: { list: subscribingList },
  } = useSelector((state) => state);
  const dispatch = useDispatch();

  const isSubscribe = subscribingList.some((subscribing: any) => subscribing.celebrityId === celebrityId) && account;

  function handleClick(event: SyntheticEvent) {
    event.stopPropagation();

    if (address === '') {
      return setShowMetaMaskDialog(true);
    }

    if (!account) {
      return setProfileDialog(true);
    }

    if (isSubscribe) {
      setOpen(true);
    } else {
      dispatch(FollowingActions.addFollowingRequest({ fanId: account?.id ?? 0, celebrityId }));
      dispatch(SubscribingActions.addSubscribingRequest({ fanId: account?.id ?? 0, celebrityId }));
      onSubscribe && onSubscribe();
    }
  }

  const handleCloseMetaMask = (event: SyntheticEvent) => {
    event.stopPropagation();

    setShowMetaMaskDialog(false);
  };

  const handleMetaMaskButton = (event: SyntheticEvent) => {
    event.stopPropagation();

    if (!isMetaMask) {
      return window.open(getMetamaskWebsiteUrl(), '_blank');
    }

    dispatch(WalletActions.connectWallet({ callback: () => handleCloseMetaMask(event) }));
  };

  const handleCloseProfile = (event: SyntheticEvent) => {
    event.stopPropagation();

    setProfileDialog(false);
  };

  const onSubmit = (values: CreateAccount) => {
    dispatch(
      AccountActions.addAccountRequest({
        data: { ...values, wallet: address, role },
        callback: () => {
          setProfileDialog(false);
        },
      }),
    );
  };

  return (
    <>
      <Button variant={!isContained ? 'outlined' : 'contained'} className={cx(classes.btn, className)} onClick={(e) => handleClick(e)}>
        {isSubscribe ? (
          <>
            <CheckIcon />
            Subscribed
          </>
        ) : (
          'Subscribe'
        )}
      </Button>
      <CancelDialog
        open={open}
        handleClose={() => setOpen(false)}
        handleSubmit={() => {
          dispatch(SubscribingActions.deleteSubscribingRequest({ fanId: account?.id ?? 0, celebrityId }));
          setOpen(false);
        }}
        title="Unsubscribe"
        subtitle="Are you sure you want to cancel your subscription? All Rare Tokens will be closed and you will not be able to buy it directly."
        cancelBtn="Cancel"
        submitBtn="Unsubscribe"
      />
      <MetaMaskModal
        open={showMetaMaskDialog}
        error={walletError || accountError}
        handleClose={handleCloseMetaMask}
        loading={metamaskLoading}
        isMetaMask={isMetaMask}
        handleButton={handleMetaMaskButton}
      />
      <ProfileDialog open={showProfileDialog} loading={loadingRole || loadingAccount} handleClose={handleCloseProfile} onSubmit={onSubmit} initValues={initValues} />
    </>
  );
}
